// Wrapper around fetch() that attaches the current Supabase session JWT to
// /api/* calls. The Pages Functions verify the JWT before invoking Gemini, so
// every call from the app must include it.

import { supabase } from "./supabase";

export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  if (!headers.has("Content-Type") && init.body) {
    headers.set("Content-Type", "application/json");
  }

  if (supabase) {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
  }

  return fetch(path, { ...init, headers });
}

/**
 * Map common Pages-Function error responses to friendlier text. Returns the
 * parsed body for 2xx, throws an Error with the user-facing message otherwise.
 */
export async function apiJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await apiFetch(path, init);
  } catch (err) {
    // Surface AbortError unchanged so callers can distinguish "user canceled"
    // from "the request hard-failed".
    if ((err as Error)?.name === "AbortError") throw err;
    throw err;
  }
  if (res.ok) return (await res.json()) as T;

  let detail: string;
  try {
    const data = (await res.json()) as { error?: string };
    detail = data?.error ?? `HTTP ${res.status}`;
  } catch {
    detail = `HTTP ${res.status}`;
  }
  // Friendly remap of well-known statuses.
  if (res.status === 401) throw new Error("Please sign in again — your session expired.");
  if (res.status === 429) throw new Error(detail || "Daily AI limit reached. Try again tomorrow.");
  if (res.status === 413) throw new Error("That image is too large — try a smaller photo.");
  throw new Error(detail);
}
